import React, { lazy, Suspense } from 'react'
import styled from 'styled-components'
import { connect } from 'react-redux'
import colors from '@sportninja/common/constants/appColors'
import {
  getPlayerTypesBySportId,
  getTeamOfficialTypesBySportId,
} from '@sportninja/common/selectors/types'
import {
  PlayerType,
  TeamOfficialType,
  RegistrationTypes,
  Tax,
} from '@sportninja/common/types/Registration'
import { RegistrationProvider } from './context/RegistrationContext'
import { UIProvider } from './context/UIContext'
import { PageLoading } from './components/shared/LoadingStates'

// Lazy load the main page component
const RegistrationPage = lazy(() => import('./RegistrationPage'))

const LoadingContainer = styled.div`
  min-height: 100vh;
  background: ${colors.SECONDARY_400};
  display: flex;
  align-items: center;
  justify-content: center;
`

interface RegistrationPageEntryOwnProps {
  scheduleId: string
  sportId?: string
  setIsRegistrationFormOpen?: (isOpen: boolean) => void
  // Props to sync data with parent Schedule component for RegistrationSetup modal

  setRegistration?: (registration: any) => void
  setRegistrationTypes?: (registrationTypes: RegistrationTypes) => void
  setAvailableTaxes?: (availableTaxes: Tax[]) => void
  setIsRegistrationSetup?: (isSetup: boolean) => void
  refetchRegistrationFn?: React.MutableRefObject<(() => void) | null>
  refetchRegistrationInfoFn?: React.MutableRefObject<(() => void) | null>
}

interface RegistrationPageEntryStateProps {
  playerTypes: PlayerType[]
  teamOfficialTypes: TeamOfficialType[]
}

type RegistrationPageEntryProps = RegistrationPageEntryOwnProps &
  RegistrationPageEntryStateProps

/**
 * Entry point for the new Registration page.
 * Wraps the page with necessary providers and handles lazy loading.
 */
const RegistrationPageEntry: React.FC<RegistrationPageEntryProps> = ({
  scheduleId,
  sportId,
  setIsRegistrationFormOpen,
  playerTypes,
  teamOfficialTypes,
  // Props for syncing with parent Schedule component
  setRegistration,
  setRegistrationTypes,
  setAvailableTaxes,
  setIsRegistrationSetup,
  refetchRegistrationFn,
  refetchRegistrationInfoFn,
}) => {
  return (
    <RegistrationProvider scheduleId={scheduleId}>
      <UIProvider>
        <Suspense
          fallback={
            <LoadingContainer>
              <PageLoading />
            </LoadingContainer>
          }
        >
          <RegistrationPage
            teamOfficialTypes={teamOfficialTypes}
            playerTypes={playerTypes}
            sportId={sportId}
            setIsRegistrationFormOpen={setIsRegistrationFormOpen}
            // Pass through parent sync props
            setRegistration={setRegistration}
            setRegistrationTypes={setRegistrationTypes}
            setAvailableTaxes={setAvailableTaxes}
            setIsRegistrationSetup={setIsRegistrationSetup}
            refetchRegistrationFn={refetchRegistrationFn}
            refetchRegistrationInfoFn={refetchRegistrationInfoFn}
          />
        </Suspense>
      </UIProvider>
    </RegistrationProvider>
  )
}

const mapStateToProps = (
  state: any,
  { sportId }: RegistrationPageEntryOwnProps
): RegistrationPageEntryStateProps => ({
  teamOfficialTypes: sportId
    ? getTeamOfficialTypesBySportId(state, sportId)
    : [],
  playerTypes: sportId ? getPlayerTypesBySportId(state, sportId) : [],
})

export default connect(mapStateToProps)(RegistrationPageEntry)
